import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Buildings, UsersThree, MapPin, CircleNotch, Sparkle, Briefcase, UserCircle, Globe,
} from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { EDITION } from "@/lib/edition";

function MemberCard({ m }) {
  return (
    <Link to={`/u/${m.public_slug || m.user_id}`} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-white p-4 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`org-member-${m.user_id}`}>
      {m.avatar_file_id ? <img src={fileUrl(m.avatar_file_id)} alt="" className="h-11 w-11 rounded-full object-cover" /> : <div className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary"><UserCircle size={26} weight="duotone" /></div>}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{m.name}</p>
        <p className="truncate text-xs text-muted-foreground">{[m.title, m.department].filter(Boolean).join(" · ") || m.headline}</p>
      </div>
    </Link>
  );
}

export default function OrganizationProfile() {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [people, setPeople] = useState({ current: [], former: [], total: 0 });
  const [err, setErr] = useState(false);

  useEffect(() => {
    setOrg(null); setErr(false);
    api.get(`/organizations/${orgId}`).then((r) => setOrg(r.data)).catch(() => setErr(true));
    api.get(`/organizations/${orgId}/people`).then((r) => setPeople(r.data)).catch(() => {});
  }, [orgId]);

  useSeo(
    org ? org.name : "Organization",
    org ? (org.about || `${org.name} — people who worked here, on ${EDITION.brand}.`) : null,
    null,
    org ? { canonicalPath: `/organizations/${orgId}`, jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: org.name, description: org.about || undefined, ...(org.website ? { sameAs: [org.website] } : {}) } } : undefined,
  );

  if (err) return <div className="mx-auto max-w-3xl px-5 py-16 text-center text-muted-foreground" data-testid="org-error">Organization not found.</div>;
  if (!org) return <div className="grid place-items-center py-24" data-testid="org-loading"><CircleNotch size={26} className="animate-spin text-primary" /></div>;

  return (
    <div className="bg-background" data-testid="organization-profile">
      <div className="border-b border-border/60 bg-gradient-to-br from-rose-50 via-background to-blue-50/50">
        <div className="mx-auto max-w-4xl px-5 py-10">
          <div className="flex items-start gap-4">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-rose-600 text-white shadow"><Buildings size={30} weight="duotone" /></span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-3xl font-bold tracking-tight text-[#1e3a5f]" data-testid="org-name">{org.name}</h1>
                {org.ai_status === "draft" && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700" data-testid="org-ai-badge"><Sparkle size={12} weight="fill" /> AI draft</span>}
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                {org.industry && <span className="flex items-center gap-1"><Briefcase size={14} /> {org.industry}</span>}
                {(org.headquarters?.city || org.headquarters?.country) && <span className="flex items-center gap-1"><MapPin size={14} /> {[org.headquarters.city, org.headquarters.country].filter(Boolean).join(", ")}</span>}
                {org.website && <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline"><Globe size={14} /> Website</a>}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-rose-700"><UsersThree size={15} /> {people.total} {people.total === 1 ? "person" : "people"} worked here</p>
            </div>
          </div>
          {org.about && <p className="mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-testid="org-about">{org.about}</p>}
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-8 px-5 py-8">
        <section data-testid="org-current">
          <h2 className="mb-3 font-heading text-lg">Current ({people.current.length})</h2>
          {people.current.length === 0 ? <p className="text-sm text-muted-foreground">No current members listed yet.</p>
            : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{people.current.map((m) => <MemberCard key={m.user_id} m={m} />)}</div>}
        </section>
        <section data-testid="org-former">
          <h2 className="mb-3 font-heading text-lg">Former / Alumni ({people.former.length})</h2>
          {people.former.length === 0 ? <p className="text-sm text-muted-foreground">Be the first to say you worked here — add it to your career on the Workmate Alumni page.</p>
            : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{people.former.map((m) => <MemberCard key={m.user_id} m={m} />)}</div>}
        </section>
      </div>
    </div>
  );
}
